/**
 * 포트빌 관리비 시스템 상수 정의
 * 
 * 모든 하드코딩된 숫자들을 중앙 집중식으로 관리하여
 * 유지보수성과 가독성을 향상시킵니다.
 */

// ==================== 기본 요금 및 비율 ====================

/**
 * 부가세율 관련 상수
 */
export const VAT_RATES = {
  /** 관리비 부가세율 (%) */
  MANAGEMENT_FEE: 10,
  
  /** 통신비 부가세율 (%) */
  COMMUNICATION_FEE: 10,
  
  /** 전기요금 부가세율 (%) */
  ELECTRICITY_FEE: 10,
  
  /** 전력기금 계산용 부가세율 (소수) */
  ELECTRICITY_FUND_VAT: 1.1,
  
  /** 일반 부가세율 (소수) */
  STANDARD_VAT: 0.1
} as const;

/**
 * 면적기여비율 기본값
 */
export const AREA_CONTRIBUTION_RATIOS = {
  /** 전기 면적기여비율 기본값 */
  ELECTRICITY_DEFAULT: 0.0040329,
  
  /** 수도 면적기여비율 기본값 */
  WATER_DEFAULT: 0.005085
} as const;

/**
 * 전력기금 계산 상수
 */
export const ELECTRICITY_FUND = {
  /** 시설물 전력기금 계산 비율 */
  FACILITY_RATE: 0.032,
  
  /** 전력기금 반올림 단위 (원) */
  ROUNDING_UNIT: 10
} as const;

/**
 * 요금 반올림 단위
 */
export const ROUNDING_UNITS = {
  /** 전기요금 반올림 단위 (원) */
  ELECTRICITY: 100,
  
  /** 수도요금 반올림 단위 (원) */
  WATER: 10,
  
  /** 상계처리비 반올림 단위 (원) */
  OFFSET: 100,
  
  /** 소수점 반올림 (공용 사용량 계산용) */
  DECIMAL_PLACES: 100
} as const;

// ==================== SMS 인증 관련 ====================

/**
 * SMS 인증 관련 상수
 */
export const SMS_AUTH = {
  /** 인증코드 자릿수 */
  CODE_LENGTH: 6,
  
  /** 인증코드 최소값 */
  CODE_MIN: 100000,
  
  /** 인증코드 최대값 */
  CODE_MAX: 999999,
  
  /** 인증코드 유효시간 (밀리초) */
  EXPIRY_TIME: 5 * 60 * 1000, // 5분
  
  /** 인증코드 유효시간 (분) */
  EXPIRY_MINUTES: 5
} as const;

// ==================== 계산 관련 상수 ====================

/**
 * 백분율 계산 상수
 */
export const PERCENTAGE = {
  /** 백분율 변환 (1 = 100%) */
  MULTIPLIER: 100,
  
  /** 소수점 반올림 정밀도 */
  PRECISION: 100
} as const;

/**
 * 기본값 설정
 */
export const DEFAULTS = {
  /** 기본 월 임대료 */
  MONTHLY_RENT: 0,
  
  /** 기본 관리비 */
  MANAGEMENT_FEE: 0,
  
  /** 기본 통신비 */
  COMMUNICATION_FEE: 0,
  
  /** 기본 호실 타입 */
  ROOM_TYPE: '포트빌' as const
} as const;

// ==================== Firestore 쿼리 제한 ====================

/**
 * 데이터베이스 쿼리 관련 상수
 */
export const QUERY_LIMITS = {
  /** 단일 문서 조회 제한 */
  SINGLE_DOCUMENT: 1,
  
  /** 기본 페이지 크기 */
  DEFAULT_PAGE_SIZE: 50,
  
  /** 최대 배치 작업 크기 */
  MAX_BATCH_SIZE: 500
} as const;

// ==================== 유틸리티 함수 ====================

/**
 * 전기요금 반올림 (100원 단위)
 */
export const roundElectricityFee = (amount: number): number => {
  return Math.round(amount / ROUNDING_UNITS.ELECTRICITY) * ROUNDING_UNITS.ELECTRICITY;
};

/**
 * 수도요금 반올림 (10원 단위 올림, 음수 처리 포함)
 */
export const roundWaterFee = (amount: number): number => {
  if (amount >= 0) {
    return Math.ceil(amount / ROUNDING_UNITS.WATER) * ROUNDING_UNITS.WATER;
  } else {
    return -Math.ceil(Math.abs(amount) / ROUNDING_UNITS.WATER) * ROUNDING_UNITS.WATER;
  }
};

/**
 * 전력기금 반올림 (10원 단위 반올림, 음수 처리 포함)
 */
export const roundElectricityFund = (amount: number): number => {
  if (amount >= 0) {
    return Math.round(amount / ELECTRICITY_FUND.ROUNDING_UNIT) * ELECTRICITY_FUND.ROUNDING_UNIT;
  } else {
    return -Math.round(Math.abs(amount) / ELECTRICITY_FUND.ROUNDING_UNIT) * ELECTRICITY_FUND.ROUNDING_UNIT;
  }
};

/**
 * 상계처리비 반올림 (100원 단위)
 */
export const roundOffsetFee = (amount: number): number => {
  return Math.round(amount / ROUNDING_UNITS.OFFSET) * ROUNDING_UNITS.OFFSET;
};

/**
 * 부가세 계산
 */
export const calculateVAT = (amount: number, vatRate: number = VAT_RATES.STANDARD_VAT): number => {
  return Math.round(amount * vatRate);
};

/**
 * 백분율 계산 (소수점 2자리)
 */
export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((value / total) * PERCENTAGE.MULTIPLIER * PERCENTAGE.PRECISION) / PERCENTAGE.PRECISION;
};

/**
 * SMS 인증코드 생성
 */
export const generateSMSCode = (): string => {
  return Math.floor(SMS_AUTH.CODE_MIN + Math.random() * (SMS_AUTH.CODE_MAX - SMS_AUTH.CODE_MIN + 1)).toString();
};

/**
 * SMS 만료 시간 계산
 */
export const getSMSExpiryTime = (): Date => {
  return new Date(Date.now() + SMS_AUTH.EXPIRY_TIME);
};
